const BRAND_CONFIG_PATH = '/brand-config.json';
const DEFAULT_BRAND_CONFIG_PATH = '/brands/default/brand-config.json';

interface BrandIdentityConfig {
  appName?: string;
  tenantId?: string;
  auth0?: {
    domain?: string;
    clientId?: string;
    redirectUris?: string[];
  };
  [key: string]: unknown;
}

interface BrandBrandingConfig {
  logoUrl?: string;
  siteTitle?: string;
  siteName?: string;
  siteUrl?: string;
  metaDescription?: string;
  ogImageUrl?: string;
  footerLogoUrl?: string;
  faviconUrl?: string;
  theme?: Record<string, unknown>;
  [key: string]: unknown;
}

interface BrandBehaviorConfig {
  featureFlags?: Record<string, boolean>;
  [key: string]: unknown;
}

interface BrandConfig {
  identity?: BrandIdentityConfig;
  branding?: BrandBrandingConfig;
  behavior?: BrandBehaviorConfig;
}

interface BrandConfigLoadError extends Error {
  primaryError?: unknown;
  fallbackError?: unknown;
}

declare global {
  interface Window {
    __BRAND_CONFIG__?: BrandConfig;
  }
}

const isBrandConfigObject = (value: unknown): value is BrandConfig =>
  Boolean(value) &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  !(typeof Element !== 'undefined' && value instanceof Element);

const readInlineBrandConfig = (): BrandConfig | null => {
  if (typeof window === 'undefined') return null;

  if (isBrandConfigObject(window.__BRAND_CONFIG__)) {
    return window.__BRAND_CONFIG__;
  }

  const inlineConfigEl =
    typeof document !== 'undefined'
      ? document.getElementById('__BRAND_CONFIG__')
      : null;
  if (!inlineConfigEl?.textContent) return null;

  try {
    const parsedConfig = JSON.parse(inlineConfigEl.textContent) as BrandConfig;
    window.__BRAND_CONFIG__ = parsedConfig;
    return parsedConfig;
  } catch {
    return null;
  }
};

let brandConfig: BrandConfig | null = readInlineBrandConfig();

const setBrandConfig = (config: BrandConfig | null): void => {
  brandConfig = config || null;
};

export const getIdentityConfig = (): BrandIdentityConfig =>
  brandConfig?.identity || {};

export const getBrandingConfig = (): BrandBrandingConfig =>
  brandConfig?.branding || {};

export const getBehaviorConfig = (): BrandBehaviorConfig =>
  brandConfig?.behavior || {};

const loadBrandConfigFrom = async (path: string): Promise<BrandConfig> => {
  const response = await fetch(path, {
    credentials: 'same-origin',
  });
  if (!response.ok) {
    throw new Error(`Failed to load ${path}: ${response.status}`);
  }
  return response.json() as Promise<BrandConfig>;
};

const formatLoadError = (err: unknown): string => {
  if (err instanceof Error) return err.message;
  return String(err);
};

export const loadBrandConfig = async (): Promise<BrandConfig> => {
  if (brandConfig) return brandConfig;

  const inlineConfig = readInlineBrandConfig();
  if (inlineConfig) {
    setBrandConfig(inlineConfig);
    return inlineConfig;
  }

  try {
    const json = await loadBrandConfigFrom(BRAND_CONFIG_PATH);
    setBrandConfig(json);
    return json;
  } catch (primaryError) {
    try {
      const fallbackJson = await loadBrandConfigFrom(DEFAULT_BRAND_CONFIG_PATH);
      setBrandConfig(fallbackJson);
      return fallbackJson;
    } catch (fallbackError) {
      const error = new Error(
        `Failed to load brand config: ${formatLoadError(primaryError)}; ${formatLoadError(fallbackError)}`
      ) as BrandConfigLoadError;
      error.primaryError = primaryError;
      error.fallbackError = fallbackError;
      throw error;
    }
  }
};
